import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import DrawingSearchPanel from "../DesignerSearchPanel";
import DrawingSnGrid from "../DesignerSnGrid";
import DesignerSnResizable from "../DesignerSnResizable";
import { useReducer, useCallback } from "react";

type PanelSizes = Record<string, string | number>;

interface Action {
  type: "SET_SIZE";
  key: string;
  value: string | number;
}

interface Props {
  loadData?: any;
}

const reducer = (state: PanelSizes, action: Action): PanelSizes => {
  switch (action.type) {
    case "SET_SIZE":
      return { ...state, [action.key]: action.value };
    default:
      return state;
  }
};

const usePanelSizes = (
  defaultSizes: PanelSizes
): [
  PanelSizes,
  (
    seq: number,
    size: string | number,
    options?: { skipStorage?: boolean }
  ) => void,
] => {
  const panelKeys = Object.keys(defaultSizes);

  const initialSizes: PanelSizes = {};
  panelKeys.forEach((key) => {
    initialSizes[key] = defaultSizes[key];
  });

  const [state, dispatch] = useReducer(reducer, initialSizes);

  const resize = useCallback(
    (seq: number, size: string | number) => {
      const key = panelKeys[seq - 1];
      if (!key) return;

      dispatch({ type: "SET_SIZE", key, value: size });
    },
    [panelKeys]
  );

  return [state, resize];
};

/**
 * 2단그리드(좌우)
 */
const TemplateReg3 = forwardRef(({ loadData }: Props, ref) => {
  const gridRef1 = useRef<any>(null);
  const gridRef2 = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColumns1] = useState<any[]>([{}]);
  const [columns2, setColumns2] = useState<any[]>([{}]);
  const [gridSettings1, setGridSettings1] = useState<any[]>([{}]);
  const [gridSettings2, setGridSettings2] = useState<any[]>([{}]);
  const defaultSize = { panel1width: "49.5%", panel2width: "49.5%" };

  const [sizes, onResizeStop] = usePanelSizes(defaultSize);
  useEffect(() => {
    if (!loadData) {
      setFormData(null);
      setFormControlData([]);
      setColumns1([{}]);
      setColumns2([{}]);
    } else {
      setFormData(loadData.formData);
      setFormControlData(loadData.formControlData);
      setColumns1(loadData.grid.grid1);
      setColumns2(loadData.grid.grid2);
      if (loadData.gridSettings?.grid1)
        setGridSettings1(loadData.gridSettings.grid1);
      if (loadData.gridSettings?.grid2)
        setGridSettings2(loadData.gridSettings.grid2);
    }
  }, [loadData]);

  useImperativeHandle(ref, () => ({
    getSaveData,
  }));

  const getSaveData = async () => {
    const searchformData = await searchRef.current.getFormData();
    const searchControls = await searchRef.current.getControls();
    const gridColumns1 = await gridRef1.current.getColumns();
    const gridColumns2 = await gridRef2.current.getColumns();
    const gridSettings1 = await gridRef1.current.getSettings();
    const gridSettings2 = await gridRef2.current.getSettings();

    const columnsInfo1: any[] = gridSettings1.columnsInfo;
    const columnsInfo2: any[] = gridSettings2.columnsInfo;
    let newDefaultValue1: any = {};
    let newDefaultValue2: any = {};

    const filteredNo = columnsInfo2.filter(
      (col) =>
        col.column_name.includes("no_line") ||
        col.column_name.includes("no_seq")
    );
    filteredNo.map((col) => {
      newDefaultValue2[col.column_name] =
        `grid2ApiRef.current.getMaxValue('${col.column_name}') + 1`;
    });

    columnsInfo1.map((col1) => {
      if (searchformData[col1.column_name] !== undefined) {
        newDefaultValue1[col1.column_name] = `searchInfo.${col1.column_name}`;
      }
      const filtered = columnsInfo2.filter(
        (col2) => col1.column_name == col2.column_name
      );
      if (filtered.length > 0) {
        if (
          !["am", "um", "qt", "dummy", "reg", "amd", "rt", "rmk"].some(
            (keyword) => col1.column_name.includes(keyword)
          )
        )
          newDefaultValue2[col1.column_name] =
            `grid1FocusedRow.${filtered[0].column_name}`;
      }
    });

    const mergedDefaultValue1 = {
      ...gridSettings1.defaultValue,
      ...newDefaultValue1,
    };

    const orderedDefaultValue1: any = {};
    columnsInfo1.forEach((col) => {
      if (mergedDefaultValue1.hasOwnProperty(col.column_name)) {
        orderedDefaultValue1[col.column_name] =
          mergedDefaultValue1[col.column_name];
      }
    });

    const mergedDefaultValue2 = {
      ...gridSettings2.defaultValue,
      ...newDefaultValue2,
    };

    const orderedDefaultValue2: any = {};
    columnsInfo2.forEach((col) => {
      if (mergedDefaultValue2.hasOwnProperty(col.column_name)) {
        orderedDefaultValue2[col.column_name] =
          mergedDefaultValue2[col.column_name];
      }
    });

    const rtn = {
      formData: searchformData,
      formControlData: searchControls,
      grid: { grid1: gridColumns1, grid2: gridColumns2 },
      gridSettings: {
        grid1: {
          ...gridSettings1,
          defaultValue: orderedDefaultValue1,
        },
        grid2: {
          ...gridSettings2,
          defaultValue: orderedDefaultValue2,
        },
      },
    };
    return rtn;
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <DrawingSearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></DrawingSearchPanel>
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <DesignerSnResizable
          direction={"width"}
          seq={1}
          size={sizes.panel1width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <DrawingSnGrid
            ref={gridRef1}
            seq={1}
            enableAddRow={true}
            columnConfig={columns1}
            rowData={[]}
            title="타이틀"
            settingsData={gridSettings1}
          />
        </DesignerSnResizable>
        <DesignerSnResizable
          direction={"width"}
          seq={2}
          size={sizes.panel2width}
          onResizeStop={onResizeStop}
          enable={{ right: true }}
          minWidth={130}
        >
          <DrawingSnGrid
            ref={gridRef2}
            seq={2}
            enableAddRow={true}
            columnConfig={columns2}
            rowData={[]}
            title="타이틀"
            settingsData={gridSettings2}
          />
        </DesignerSnResizable>
      </div>
    </div>
  );
});

export default TemplateReg3;
